import { defineComponent as _defineComponent } from 'vue'
import { ref, onUnmounted, watch, getCurrentInstance, computed } from 'vue';
import { useRouter } from 'vue-router/composables';
import { SupplierImportModel, type ImportJobStatus, type ProcessedSupplierSummary } from '@/models/supplier_import';
import { EventBus } from '@/main';
import CopyLinkButton from '@/components/CopyLinkButton.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'SupplierImportUpload',
  setup(__props) {

const POLLING_INTERVAL = 5000; // 5 seconds
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const vm = getCurrentInstance();
const router = vm?.proxy.$router;

const selectedFileLocal = ref<File | null>(null);
const fileInput = ref<any>(null);
const isDragover = ref(false);

const isLoadingTemplate = ref(false);
const templateDownloadError = ref<string | null>(null);

const isLoadingUnlinkedTemplate = ref(false);
const unlinkedTemplateError = ref<string | null>(null);

const isUploading = ref(false);
const uploadError = ref<string | null>(null);
const uploadSuccessMessage = ref<string | null>(null);

const currentJobId = ref<string | null>(null);
const currentJobStatus = ref<ImportJobStatus | null>(null);
const isPollingStatus = ref(false);
const pollingError = ref<string | null>(null);
let pollingIntervalId: number | undefined = undefined;

const isProcessing = ref(false);
const processError = ref<string | null>(null);
const processSuccessMessage = ref<string | null>(null);

const panel = ref(0);

const uppercasedJobStatus = computed(() => {
  return currentJobStatus.value ? currentJobStatus.value.status.toUpperCase() : null;
});

const isJobProcessing = computed(() => {
  if (!currentJobId.value) return false;
  if (!currentJobStatus.value) return true; 
  const status = currentJobStatus.value.status.toUpperCase();
  return !['COMPLETED', 'ERROR', 'PARTIAL_SUCCESS'].includes(status);
});

const totalProductsLinked = computed(() => {
  if (currentJobStatus.value?.results?.processedSummary?.length) {
    return currentJobStatus.value.results.processedSummary.reduce((total: number, summary: ProcessedSupplierSummary) => total + (summary.productsLinked || 0), 0);
  }
  return 0;
});

const triggerFileInput = () => {
  const nativeInputElement = (fileInput.value as any)?.$el?.querySelector('input[type="file"]');
  if (nativeInputElement) {
    nativeInputElement.click();
  }
};

const clearFile = () => {
  selectedFileLocal.value = null;
  uploadError.value = null;
  if(fileInput.value) {
    (fileInput.value as any).clearableCallback();
  }
}

const handleDragOver = (event: DragEvent) => {
  isDragover.value = true;
};

const handleDragLeave = (event: DragEvent) => {
  isDragover.value = false;
};

const handleDrop = (event: DragEvent) => {
  isDragover.value = false;
  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    const file = files[0];
    if (file.size > MAX_FILE_SIZE_BYTES) {
      const errorMessage = `File size exceeds the ${MAX_FILE_SIZE_MB}MB limit.`;
      uploadError.value = errorMessage;
      selectedFileLocal.value = null;
      EventBus.$emit('show-snackbar', { message: errorMessage, color: 'error' });
      return;
    }
    if(file.type === 'text/csv' || file.name.endsWith('.csv')) {
      selectedFileLocal.value = file;
      uploadError.value = null; 
    } else {
      uploadError.value = "Invalid file type. Please upload a .csv file.";
      selectedFileLocal.value = null;
    }
  }
};

// Handle file selection from v-file-input
const handleFileSelectedViaInput = (file: File | null) => {
  if (file && file.size > MAX_FILE_SIZE_BYTES) {
    const errorMessage = `File size exceeds the ${MAX_FILE_SIZE_MB}MB limit.`;
    uploadError.value = errorMessage;
    clearFile();
    EventBus.$emit('show-snackbar', {message: errorMessage, color: 'error'});
    return;
  }
  
  selectedFileLocal.value = file;
  if (file) {
    uploadError.value = null; // Clear previous upload error
  } 
};

const downloadTemplateFile = (filename: string, content: string, contentType: string) => {
  const blob = new Blob([content], { type: contentType });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

async function downloadTemplate() {
  isLoadingTemplate.value = true;
  templateDownloadError.value = null;
  try {
    const templateString = await SupplierImportModel.getImportTemplate();
    if (templateString) {
      downloadTemplateFile('supplier_import_template.csv', templateString, 'text/csv;charset=utf-8;');
    } else {
      templateDownloadError.value = 'Received empty template from server.';
    }
  } catch (error: any) {
    console.error('Failed to download template:', error);
    templateDownloadError.value = error.message || 'An unknown error occurred.';
  } finally {
    isLoadingTemplate.value = false;
  }
}

async function downloadUnlinkedSkuTemplate() {
  isLoadingUnlinkedTemplate.value = true;
  unlinkedTemplateError.value = null;
  try {
    const templateString = await SupplierImportModel.getUnlinkedSkusTemplate();
    if (templateString) {
      downloadTemplateFile('unlinked_skus_template.csv', templateString, 'text/csv;charset=utf-8;');
    } else {
      unlinkedTemplateError.value = 'Received empty template from server.';
    }
  } catch (error: any) {
    console.error('Failed to download unlinked SKUs template:', error);
    unlinkedTemplateError.value = error.message || 'An unknown error occurred.';
  } finally {
    isLoadingUnlinkedTemplate.value = false;
  }
}

async function uploadFile() {
  if (!selectedFileLocal.value) {
    uploadError.value = 'Please select a CSV file to upload.';
    return;
  }

  isUploading.value = true;
  uploadError.value = null;
  uploadSuccessMessage.value = null;
  processError.value = null;
  processSuccessMessage.value = null;
  currentJobId.value = null;
  currentJobStatus.value = null;
  stopPollingJobStatus();

  try {
    const response = await SupplierImportModel.uploadSupplierCSV(selectedFileLocal.value);
    uploadSuccessMessage.value = response.message;
    currentJobId.value = response.jobId;
    selectedFileLocal.value = null; // Reset file input

    // Automatically trigger the processing job
    await triggerProcessJob();

  } catch (error: any) {
    console.error('File upload failed:', error);
    uploadError.value = error.response?.data?.message || error.message || 'An unknown error occurred during upload.';
    currentJobId.value = null;
  } finally {
    isUploading.value = false;
  }
}

async function fetchJobStatus() {
  if (!currentJobId.value) return;
  isPollingStatus.value = true;
  pollingError.value = null;
  try {
    const status = await SupplierImportModel.getImportJobStatus(currentJobId.value);
    currentJobStatus.value = status;
    if (status && (status.status.toUpperCase() === 'COMPLETED' || status.status.toUpperCase() === 'ERROR' || status.status.toUpperCase() === 'PARTIAL_SUCCESS')) {
      stopPollingJobStatus();
    }
  } catch (error: any) {
    console.error('Failed to fetch job status:', error);
    pollingError.value = error.message || 'Could not retrieve job status.';
  } finally {
    isPollingStatus.value = false;
  }
}

function startPollingJobStatus(jobIdToPoll: string) {
  if (pollingIntervalId) stopPollingJobStatus();
  if (!jobIdToPoll) return;
  currentJobId.value = jobIdToPoll;
  fetchJobStatus();
  pollingIntervalId = window.setInterval(fetchJobStatus, POLLING_INTERVAL);
}

function stopPollingJobStatus() {
  if (pollingIntervalId) {
    clearInterval(pollingIntervalId);
    pollingIntervalId = undefined;
  }
}

async function triggerProcessJob() {
  if (!currentJobId.value) {
    processError.value = 'No Job ID found to process.';
    return;
  }
  isProcessing.value = true;
  processError.value = null;
  processSuccessMessage.value = null;
  try {
    const response = await SupplierImportModel.processSupplierImport(currentJobId.value);
    processSuccessMessage.value = response.message;
    await fetchJobStatus();
    if (currentJobStatus.value && !['COMPLETED', 'ERROR', 'PARTIAL_SUCCESS'].includes(currentJobStatus.value.status.toUpperCase())) {
      if (!pollingIntervalId) startPollingJobStatus(currentJobId.value);
    } else if (pollingIntervalId) {
      stopPollingJobStatus();
    }
  } catch (error: any) {
    console.error('Failed to trigger process job:', error);
    processError.value = error.response?.data?.message || error.message || 'An unknown error occurred during processing.';
  } finally {
    isProcessing.value = false;
  }
}

watch(currentJobId, (newJobId, oldJobId) => {
  if (newJobId && newJobId !== oldJobId) {
    currentJobStatus.value = null;
    processError.value = null;
    processSuccessMessage.value = null;
    startPollingJobStatus(newJobId);
  } else if (!newJobId) {
    stopPollingJobStatus();
    currentJobStatus.value = null;
  }
});

const goToSupplierList = () => {
  if (router) {
    router.push({ name: 'suppliers' });
  }
};

const getJobStatusColor = (status: ImportJobStatus['status']): string => {
  if (!status) return 'grey';

  switch (status.toUpperCase()) {
    case 'PENDING': return 'blue';
    case 'PROCESSING': return 'orange';
    case 'COMPLETED': return 'green';
    case 'ERROR': return 'red';
    case 'PARTIAL_SUCCESS': return 'deep-orange';
    case 'UPLOADED_VALIDATED': return 'info';
    default: return 'grey';
  }
};

const getSummaryStatusColor = (status: ProcessedSupplierSummary['status']) => {
  if (!status) return 'grey';
  switch (status.toUpperCase()) {
    case 'PENDING': return 'blue';
    case 'PROCESSING': return 'orange';
    case 'COMPLETED': return 'green';
    case 'ERROR': return 'red';
    case 'PARTIAL_SUCCESS': return 'deep-orange';
    case 'UPDATED': return 'green';
    case 'CREATED': return 'green';
    default: return 'grey';
  }
};

onUnmounted(() => {
  stopPollingJobStatus();
});


return { __sfc: true,POLLING_INTERVAL, MAX_FILE_SIZE_MB, MAX_FILE_SIZE_BYTES, vm, router, selectedFileLocal, fileInput, isDragover, isLoadingTemplate, templateDownloadError, isLoadingUnlinkedTemplate, unlinkedTemplateError, isUploading, uploadError, uploadSuccessMessage, currentJobId, currentJobStatus, isPollingStatus, pollingError, pollingIntervalId, isProcessing, processError, processSuccessMessage, panel, uppercasedJobStatus, isJobProcessing, totalProductsLinked, triggerFileInput, clearFile, handleDragOver, handleDragLeave, handleDrop, handleFileSelectedViaInput, downloadTemplateFile, downloadTemplate, downloadUnlinkedSkuTemplate, uploadFile, fetchJobStatus, startPollingJobStatus, stopPollingJobStatus, triggerProcessJob, goToSupplierList, getJobStatusColor, getSummaryStatusColor, CopyLinkButton }
}

})